import { useState, useCallback, useEffect } from 'react'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { useScrollToElement } from '@/hooks/useScrollToElement'
import { useToggle } from '@/hooks/useToggle'
import { PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { SweepstakeObject } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { GiveawayTabEnum } from '@/views/PreSales/PreSalesGiveawayView/config/types'
import { GiveawayType } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

interface UseGiveawayStateParams {
  theatricalSlug: string
  tickets?: ShopifyProductWithBundles[]
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  giveawayData: GiveawayType
  experiments?: ServerExperiments
}

export const useGiveawayState = ({
  theatricalSlug,
  tickets,
  sweepstake,
  phase,
  giveawayData,
  experiments,
}: UseGiveawayStateParams) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isBackgroundVideoPaused, togglePauseBackgroundVideo] = useToggle(false)
  const [isShareModalOpen, setIsShareModalOpen] = useToggle(false)
  const [isScenePlayerOpen, setIsScenePlayerOpen] = useState(false)

  const { push, query } = useSafeRouter()

  const { ref: tabsContainerRef, scrollToElement: scrollToTopTabs } = useScrollToElement<HTMLDivElement>()
  const { ref: mobileTicketContainerRef, scrollToElement: scrollToMobileElement } = useScrollToElement<HTMLDivElement>()
  const { ref: desktopTicketContainerRef, scrollToElement: scrollToDesktopElement } =
    useScrollToElement<HTMLDivElement>()
  const { ref: heroContainerRef } = useScrollToElement<HTMLDivElement>()

  const { ref: detailsContainerRef, scrollToElement: scrollToDetailsElement } = useScrollToElement<HTMLDivElement>()
  const { ref: prizesContainerRef } = useScrollToElement<HTMLDivElement>()
  const { ref: faqContainerRef, scrollToElement: scrollToFaqElement } = useScrollToElement<HTMLDivElement>()

  const isActive = sweepstake.status === 'LIVE'

  const entryVariantIdMap: Record<string, string> = {
    [slugs.youngWashington]: '51101924688103',
    [slugs.sketch]: '51086103609575',
    [slugs.senior]: '51232328548583',
    [slugs.truthAndTreason]: '51330333540583',
    [slugs.david]: '51423755895015',
    [slugs.iWasAStranger]: '51508321288423',
    [slugs.soloMio]: '51575061184743',
  }

  const entryVariantId = entryVariantIdMap[theatricalSlug] || '51101924688103'
  const sceneId = getItemOrFirstEntry(query?.sceneId)
  const projectName = sweepstake.projectName

  useEffect(() => {
    if (query.share) {
      setIsShareModalOpen(true)
    }
  }, [query.share, setIsShareModalOpen])

  useEffect(() => {
    if (sceneId) {
      setIsScenePlayerOpen(true)
      togglePauseBackgroundVideo(true)
    }
  }, [sceneId, togglePauseBackgroundVideo])

  // Action handlers
  const scrollToProducts = useCallback(() => {
    scrollToMobileElement()
    scrollToDesktopElement()
  }, [scrollToDesktopElement, scrollToMobileElement])

  const scrollToDetails = useCallback(() => {
    scrollToDetailsElement()
  }, [scrollToDetailsElement])

  const scrollToFaq = useCallback(() => {
    scrollToFaqElement()
  }, [scrollToFaqElement])

  const scrollToTab = useCallback(
    (tabType: GiveawayTabEnum) => {
      const nextTabIndex = giveawayData?.tabs.findIndex((tab) => tab.type === tabType)
      if (nextTabIndex == null) return
      setSelectedIndex(nextTabIndex)
      scrollToTopTabs()
    },
    [giveawayData?.tabs, scrollToTopTabs],
  )

  const scrollToPrizes = useCallback(() => {
    scrollToTab(GiveawayTabEnum.Prizes)
  }, [scrollToTab])

  const handleRefundInfoClick = useCallback(() => {
    scrollToTab(GiveawayTabEnum.Faq)
    push('#refundPolicy')
  }, [push, scrollToTab])

  return {
    // State
    selectedIndex,
    setSelectedIndex,
    isBackgroundVideoPaused,
    togglePauseBackgroundVideo,
    isShareModalOpen,
    setIsShareModalOpen,
    isScenePlayerOpen,
    setIsScenePlayerOpen,

    // Data
    theatricalSlug,
    tickets,
    sweepstake,
    phase,
    giveawayData,
    projectName,
    backgroundVideoUrl: giveawayData.backgroundVideoUrl,
    galleryImages: giveawayData.galleryImages,
    experiments,

    // Computed values
    isActive,
    entryVariantId,

    // Actions
    scrollToProducts,
    scrollToPrizes,
    scrollToDetails,
    scrollToFaq,
    scrollToTab,
    handleRefundInfoClick,

    // Refs
    tabsContainerRef,
    mobileTicketContainerRef,
    desktopTicketContainerRef,
    heroContainerRef,
    detailsContainerRef,
    prizesContainerRef,
    faqContainerRef,
  }
}
